import { useRef, useEffect } from 'react';
import { useNode } from '@craftjs/core';
import { cn } from '../../../../lib/utils';

interface HeadingBlockProps {
  text: string;
  level: 1 | 2 | 3 | 4;
  fontFamily: string;
  color: string;
  textAlign: string;
}

const levelClasses: Record<number, string> = {
  1: 'text-4xl font-bold',
  2: 'text-3xl font-bold',
  3: 'text-2xl font-semibold',
  4: 'text-xl font-semibold',
};

export function HeadingBlock({ text, level, fontFamily, color, textAlign }: HeadingBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const contentRef = useRef<HTMLElement | null>(null);

  // Sync text content when not editing to avoid losing cursor position
  useEffect(() => {
    if (contentRef.current && !selected) {
      contentRef.current.textContent = text;
    }
  }, [text, selected]);

  const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4';

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn(
        'min-h-[40px] p-2 rounded transition-all',
        selected && 'ring-2 ring-primary ring-offset-2'
      )}
    >
      <Tag
        ref={(el) => { contentRef.current = el; }}
        contentEditable={selected}
        suppressContentEditableWarning
        className={cn('outline-none', levelClasses[level])}
        style={{
          fontFamily,
          color,
          textAlign: textAlign as React.CSSProperties['textAlign'],
        }}
        onInput={(e) => {
          setProp((props: HeadingBlockProps) => {
            props.text = e.currentTarget.textContent || '';
          });
        }}
      />
    </div>
  );
}

HeadingBlock.craft = {
  displayName: 'Título',
  props: {
    text: 'Título do bloco',
    level: 2 as 1 | 2 | 3 | 4,
    fontFamily: 'inherit',
    color: '#ffffff',
    textAlign: 'left',
  },
  rules: { canDrag: () => true },
};
